import { ref, onMounted, onBeforeUnmount, computed, useHost } from "vue";
import type { GroupProps } from "./group.types";
import { groupClasses } from "./group.styles";
import { mergeClassProp } from "@/runtime/style-engine/define-variants";
import { watchAndRemoveWhitespace } from "@/runtime/shared/removeWhitespaceNodes";
import { createEventDispatcher } from "@/runtime/shared/events";
import { useSharedState } from "composables/useSharedState";

export interface UseGroupOptions {
  props: Readonly<GroupProps>;
}

/**
 * WeakMap registry for parent group lookup.
 * Maps group element to its size for child component coordination.
 */
const groupRegistry = new WeakMap<Element, string>();

/**
 * Registers a group element with its size.
 */
export const registerGroup = (element: Element, size: string): void => {
  groupRegistry.set(element, size);
};

/**
 * Gets the size of a registered group element.
 */
export const getGroupSize = (element: Element): string | undefined => {
  return groupRegistry.get(element);
};

interface ApplyContext {
  size: string | undefined;
  orientation: string | undefined;
  isFirst: boolean;
  isLast: boolean;
}

const PART_RADIUS_CLASSES = [
  "part-[base]:rounded-r-none!",
  "part-[base]:rounded-l-none!",
  "part-[base]:rounded-t-none!",
  "part-[base]:rounded-b-none!",
  "part-[base]:rounded-none!",
] as const;

const applyToElement = (element: Element, ctx: ApplyContext): void => {
  const dispatch = createEventDispatcher(element);
  const isVertical = ctx.orientation === "vertical";

  if (ctx.size) {
    element.setAttribute("size", ctx.size);
    element.setAttribute("data-group-size", ctx.size);
    dispatch("update:size", ctx.size);
  }

  if (isVertical) {
    element.setAttribute("block", "true");
    dispatch("update:block", true);
  } else {
    element.removeAttribute("block");
    dispatch("update:block", false);
  }

  element.classList.toggle("group-first", ctx.isFirst);
  element.classList.toggle("group-last", ctx.isLast);
  element.classList.toggle("group-between", !ctx.isFirst && !ctx.isLast);

  if (ctx.orientation) {
    element.setAttribute("data-group-orientation", ctx.orientation);
  }
};

/**
 * Stamps part-[base]:rounded-*-none on the leaf CE so global CSS targets its rendered ::part(base).
 *
 * Contract: every CE child of e-group must expose `part="base"` on its root visual element.
 * The corresponding Tailwind classes are pre-generated via `@source inline(...)` in main.css —
 * all five combinations (rounded-r-none, rounded-l-none, rounded-t-none, rounded-b-none, rounded-none)
 * cover every position that useGroup can stamp. No responsive or state variants are used.
 */
const applyPartRadius = (element: Element, ctx: ApplyContext): void => {
  element.classList.remove(...PART_RADIUS_CLASSES);
  if (ctx.isFirst && ctx.isLast) return;

  const isVertical = ctx.orientation === "vertical";
  if (ctx.isFirst) {
    element.classList.add(isVertical ? "part-[base]:rounded-b-none!" : "part-[base]:rounded-r-none!");
  } else if (ctx.isLast) {
    element.classList.add(isVertical ? "part-[base]:rounded-t-none!" : "part-[base]:rounded-l-none!");
  } else {
    element.classList.add("part-[base]:rounded-none!");
  }
};

const applyToChild = (child: Element, ctx: ApplyContext): void => {
  applyToElement(child, ctx);
  applyPartRadius(child, ctx);
};

/**
 * Core logic for the Group component.
 * Registers props via useSharedState internally.
 */
export const useGroup = (options: UseGroupOptions) => {
  const { props } = options;
  const host = useHost();
  const state = useSharedState(props);
  const contentRef = ref<HTMLElement | null>(null);

  const classes = computed(() => mergeClassProp(groupClasses({ orientation: props.orientation }), props.class));
  let cleanupWhitespace: (() => void) | undefined;
  let isUpdating = false;

  const updateChildren = () => {
    if (isUpdating) return;
    isUpdating = true;

    try {
      const container = contentRef.value;
      if (!container) return;
      const slot = container.querySelector("slot") as HTMLSlotElement | null;
      if (!slot) return;
      const children = slot.assignedElements();
      if (children.length === 0) return;

      for (const [index, child] of children.entries()) {
        applyToChild(child, {
          size: props.size,
          orientation: props.orientation,
          isFirst: index === 0,
          isLast: index === children.length - 1,
        });
      }
    } finally {
      isUpdating = false;
    }
  };

  onMounted(() => {
    if (!host) return;
    if (props.size) registerGroup(host, props.size);
    cleanupWhitespace = watchAndRemoveWhitespace(host);

    updateChildren();

    const container = contentRef.value;
    if (!container) return;
    const slot = container.querySelector("slot") as HTMLSlotElement | null;
    if (slot) slot.addEventListener("slotchange", updateChildren);
  });

  onBeforeUnmount(() => {
    const container = contentRef.value;
    if (!container) return;
    const slot = container.querySelector("slot") as HTMLSlotElement | null;
    if (slot) slot.removeEventListener("slotchange", updateChildren);
    cleanupWhitespace?.();
  });

  return { classes, contentRef, state };
};
